import { useEffect, useMemo, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGoBack } from '@/lib/goBack';
import { Avatar } from '@/components/Avatar';
import { BackIcon, CheckIcon, CloseIcon, HelpIcon, MoreIcon, NewChatIcon, NoteIcon, SendIcon, ClockIcon, StarIcon } from '@/components/Icons';
import { SheetOption, useOverlay } from '@/components/Overlay';
import { TextSheet } from '@/components/TextSheet';
import { splitLinkButton } from '@/data/live';
import { Switch } from '@/components/Switch';
import { AiLeo } from '@/components/AiLeo';
import {
  loadBench, loadItems, loadResults, practiceSay, ROLES, saveScore, saveVerdict, SCORE_POINTS, scoreTotal, startBench, whoAmI,
  type BenchRun, type BenchTurn, type MyVerdict, type Scorecard, type Scores, type PracticeLine, type PracticeReply, type ResultRow, type ReviewItem,
} from '@/lib/training';

// Dr C practice chat (Gary, 17 Oct): you are the customer in a chat that looks like WhatsApp, the real
// Dr C answers, and you tap thumbs up or down under each reply (down asks what was wrong). The menu has a
// new chat, after hours, existing customer, ideas, and a comment on the screen itself. Head office also
// reviews the wording and sees everyone's results from the menu. Nothing here reaches WhatsApp.

type View = 'home' | 'wording' | 'drc' | 'feedback' | 'practice' | 'results' | 'bench';
const VIEWS: View[] = ['home', 'wording', 'drc', 'feedback', 'practice', 'results', 'bench'];

const SCREENS = ['Chat list', 'A chat', 'Leo', 'Alerts', 'Settings', 'Other'];

export function TrainingScreen() {
  const goBack = useGoBack();
  const { toast } = useOverlay();
  // Each Training screen is a step in history (?v=), so the phone's Back returns to Training home, and
  // from the practice chat to Leo training, as the back arrow does (mobile review, 19 Oct).
  const [params, setParams] = useSearchParams();
  const asked = params.get('v') as View | null;
  const view: View = asked && VIEWS.includes(asked) ? asked : 'home';
  const setView = (v: View) => setParams({ v });
  // The practice chat stays as it was when you step back to Leo training and open it again.
  const practice = useRef<PracticeState>({ turns: [], afterHours: false, customer: false, role: null });
  const [data, setData] = useState<{ round: number; headOffice: boolean; items: ReviewItem[]; mine: MyVerdict[] } | null>(null);
  const [testNumber, setTestNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [screen, setScreen] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    loadItems()
      .then((r) => setData({ round: r.round, headOffice: r.head_office, items: r.items, mine: r.mine }))
      .catch((e: Error) => setError(e.message));
    void whoAmI().then((w) => setTestNumber(w.test_number));
  }, []);

  if (view === 'practice') return <PracticeChat blocked={error} saved={practice} onBack={() => goBack('/training?v=drc')} />;

  async function sendFeedback() {
    if (!feedback.trim() || busy) return;
    setBusy(true);
    try {
      await saveVerdict({ target_type: 'comment', target_ref: `screen:${crypto.randomUUID()}`, target_text: screen ?? 'Not said', verdict: 'note', comment: feedback.trim(), context: { screen } });
      setFeedback('');
      setScreen(null);
      toast('Thanks, feedback saved');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const checked = new Set((data?.mine ?? []).map((m) => `${m.target_type}:${m.target_ref}`)).size;
  const title = { home: 'Training', wording: 'Approve messages', drc: 'Leo training', feedback: 'App feedback', results: 'Results', bench: 'Stress test', practice: '' }[view];
  const waLink = testNumber ? `https://wa.me/${testNumber}?text=${encodeURIComponent('TEST LEAD Hi')}` : null;
  return (
    <>
      <header className="chats-topbar chats-topbar--conv">
        {/* Inside Training the back arrow shows on a computer too, where the usual one is hidden (Gary, 17 Oct). */}
        <button type="button" className={view === 'home' ? 'chats-icon-btn chats-back' : 'chats-icon-btn'} aria-label={view === 'home' ? 'Back' : 'Back to Training'} onClick={() => goBack(view === 'home' ? '/settings' : '/training')}><BackIcon /></button>
        <h1 className="chats-topbar-title">{title}</h1>
      </header>
      <main className="chats-screen chats-screen--grey chats-train">
        {error && <p className="chats-empty">{error}</p>}
        {view === 'home' && <>
          <p className="chats-help-intro">We are stress testing the new WhatsApp setup before it goes live. Three jobs, in any order. Nothing here reaches a real customer.</p>
          <div className="chats-group">
            <button type="button" className="chats-set" onClick={() => setView('wording')}>
              <div className="chats-set-main"><b>1. Approve messages</b><small>Every message customers get from us. Approve, edit or reject. {data ? `${checked} of ${data.items.length} done.` : ''}</small></div>
            </button>
            <button type="button" className="chats-set" onClick={() => setView('drc')}>
              <div className="chats-set-main"><b>2. Leo training</b><small>Chat with Leo on WhatsApp as a customer, then rate every reply in CMB Chats.</small></div>
            </button>
            <button type="button" className="chats-set" onClick={() => setView('feedback')}>
              <div className="chats-set-main"><b>3. App feedback</b><small>Anything about how CMB Chats looks or works.</small></div>
            </button>
            {data?.headOffice && (
              <button type="button" className="chats-set" onClick={() => setView('results')}>
                <div className="chats-set-main"><b>Results</b><small>Head office: everyone's approvals, thumbs and feedback.</small></div>
              </button>
            )}
            {data && (
              <button type="button" className="chats-set" onClick={() => setView('bench')}>
                <div className="chats-set-main"><b>Stress test</b><small>AI customers chatting with Leo. Read, rate and comment.</small></div>
              </button>
            )}
          </div>
        </>}

        {view === 'wording' && data && <Wording items={data.items} mine={data.mine} onSaved={(v) => setData({ ...data, mine: [...data.mine, v] })} />}

        {view === 'drc' && <>
          <p className="chats-help-intro">You are the customer, on your own phone, in the real WhatsApp. Leo answers for real. Try to catch it out: prices, booking, a complaint, a joke, Afrikaans.</p>
          <div className="chats-group chats-pad-group">
            <p><b>1.</b> Tap the button. WhatsApp opens with "TEST LEAD Hi" ready. Send it, then chat as a customer. Start each new test with TEST LEAD.</p>
            {waLink
              ? <a className="chats-btn chats-train-wa" href={waLink} target="_blank" rel="noopener noreferrer">Open WhatsApp</a>
              : <p className="chats-empty">Loading our WhatsApp number...</p>}
            <p><b>2.</b> The same chat appears in CMB Chats, Chats tab. Under each Leo message tap 👍 or 👎. A 👎 asks what was wrong.</p>
            <p><b>3.</b> Use CMB Chats as you normally would: take over, Suggest a reply, notes. Tell us in App feedback what felt wrong.</p>
          </div>
          <p className="chats-group-title">No phone to hand?</p>
          <div className="chats-group">
            <button type="button" className="chats-set" onClick={() => setView('practice')}>
              <div className="chats-set-main">Practice here instead<small>The same Leo in a chat on this screen, with 👍 and 👎.</small></div>
            </button>
          </div>
        </>}

        {view === 'feedback' && <>
          <p className="chats-group-title">Which part of the app?</p>
          <div className="chats-chips chats-train-roles">
            {SCREENS.map((x) => <button key={x} type="button" className={`chats-chip${screen === x ? ' active' : ''}`} onClick={() => setScreen(x)}>{x}</button>)}
          </div>
          <div className="chats-group chats-pad-group">
            <textarea className="chats-input chats-input--stack" rows={5} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="What looked wrong, felt slow or confusing, or could be better?" aria-label="App feedback" />
            <button type="button" className="chats-btn" disabled={busy || !feedback.trim()} onClick={() => void sendFeedback()}>Send feedback</button>
          </div>
        </>}

        {view === 'results' && data?.headOffice && <Results />}
        {view === 'bench' && data && <Bench headOffice={data.headOffice} />}
        {!data && !error && <p className="chats-empty">Loading...</p>}
      </main>
    </>
  );
}

// ---------------------------------------------------------------- the practice chat

interface Turn { id: string; role: 'customer' | 'drc' | 'hint'; text: string; meta?: PracticeReply; verdict?: 'tick' | 'cross' }
interface PracticeState { turns: Turn[]; afterHours: boolean; customer: boolean; role: string | null }

function PracticeChat({ blocked, saved, onBack }: { blocked: string | null; saved: MutableRefObject<PracticeState>; onBack: () => void }) {
  const { toast, openSheet, closeSheet } = useOverlay();
  const [turns, setTurns] = useState<Turn[]>(saved.current.turns);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [afterHours, setAfterHours] = useState(saved.current.afterHours);
  const [customer, setCustomer] = useState(saved.current.customer);
  const [role, setRole] = useState<string | null>(saved.current.role);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    saved.current = { turns, afterHours, customer, role };
  }, [saved, turns, afterHours, customer, role]);

  useEffect(() => {
    if (box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [turns.length, busy]);

  function newChat(next?: { label: string; hint: string; afterHours?: boolean; customer?: boolean }) {
    setTurns(next ? [{ id: crypto.randomUUID(), role: 'hint', text: `Your role: ${next.label}. ${next.hint}` }] : []);
    setRole(next?.label ?? null);
    if (next) { setAfterHours(!!next.afterHours); setCustomer(!!next.customer); }
  }

  async function send(e: FormEvent) {
    e.preventDefault();
    const text = typed.trim();
    if (!text || busy) return;
    const next: Turn[] = [...turns, { id: crypto.randomUUID(), role: 'customer', text }];
    setTurns(next);
    setTyped('');
    setBusy(true);
    try {
      const lines: PracticeLine[] = next.filter((t) => t.role !== 'hint').map((t) => ({ role: t.role as 'customer' | 'drc', text: t.text }));
      // After a hand over Dr C keeps helping without handing over again, as on WhatsApp.
      const handed = next.find((t) => t.meta?.handed_over)?.meta;
      const r = await practiceSay(lines, afterHours, customer, null, handed ? (handed.summary || handed.reason || 'their question') : null);
      setTurns((t) => [...t, { id: crypto.randomUUID(), role: 'drc', text: r.text, meta: r }]);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Leo did not answer. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function judge(turn: Turn, verdict: 'tick' | 'cross', comment: string) {
    const i = turns.findIndex((t) => t.id === turn.id);
    const before = turns.slice(Math.max(0, i - 6), i).filter((t) => t.role !== 'hint').map((t) => `${t.role === 'customer' ? 'Customer' : 'Leo'}: ${t.text}`);
    try {
      await saveVerdict({
        target_type: 'practice', target_ref: turn.id, target_text: turn.text, verdict, comment: comment || undefined,
        context: { before, role, after_hours: afterHours, existing_customer: customer, handed_over: turn.meta?.handed_over ?? false, reason: turn.meta?.reason ?? null, tools: turn.meta?.tools ?? [] },
      });
      setTurns((t) => t.map((x) => (x.id === turn.id ? { ...x, verdict } : x)));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'That did not save. Try again.');
    }
  }

  function whyNotGood(turn: Turn) {
    openSheet(<TextSheet title="What was wrong with this reply?" placeholder="A few words: too long, wrong price, sounds like a robot..." onSend={async (why) => { await judge(turn, 'cross', why); closeSheet(); }} />);
  }

  function commentSheet() {
    openSheet(<TextSheet title="Comment on this screen" placeholder="Anything about how it looks or works" onSend={async (text) => {
      try {
        await saveVerdict({ target_type: 'comment', target_ref: `screen:${crypto.randomUUID()}`, target_text: 'Practice chat screen', verdict: 'note', comment: text, context: { role, after_hours: afterHours, existing_customer: customer, messages: turns.length } });
        closeSheet();
        toast('Thanks, comment saved');
      } catch (err) {
        toast(err instanceof Error ? err.message : 'That did not save. Try again.');
      }
    }} />);
  }

  function ideasSheet() {
    openSheet(
      <>
        <h2 className="chats-sheet-title">Ideas: play one in your own words</h2>
        {ROLES.map((r) => <SheetOption key={r.label} icon={<StarIcon />} title={r.label} sub={r.hint} onClick={() => { closeSheet(); newChat(r); }} />)}
      </>,
    );
  }

  function menu() {
    openSheet(
      <>
        <SheetOption icon={<NewChatIcon />} title="New chat" sub="Start again as a new customer" onClick={() => { closeSheet(); newChat(); }} />
        <SheetOption icon={<HelpIcon />} title="Ideas for what to ask" sub="Roles to play, from easy to tricky" onClick={ideasSheet} />
        <SheetOption icon={<ClockIcon />} title={afterHours ? 'After hours: on' : 'After hours: off'} sub="Leo answers as if the team is away" onClick={() => { closeSheet(); setAfterHours(!afterHours); }} />
        <SheetOption icon={<CheckIcon />} title={customer ? 'Existing customer: on' : 'Existing customer: off'} sub="Leo treats you as someone we have serviced before" onClick={() => { closeSheet(); setCustomer(!customer); }} />
        <SheetOption icon={<NoteIcon />} title="Comment on this screen" sub="How it looks or works" onClick={commentSheet} />
        {/* Leaves once the sheet's own history step is gone, so this is one step back, as the arrow. */}
        <SheetOption icon={<CloseIcon />} title="Leave practice" sub="Back to Training" onClick={() => { window.addEventListener('popstate', onBack, { once: true }); closeSheet(); }} />
      </>,
    );
  }

  const status = [afterHours ? 'after hours' : '', customer ? 'existing customer' : ''].filter(Boolean).join(', ');
  return (
    <>
      <header className="chats-topbar chats-topbar--conv">
        <button type="button" className="chats-icon-btn" aria-label="Leave practice" onClick={onBack}><BackIcon /></button>
        <div className="chats-conv-head">
          <Avatar name="CleanMyBed" colour={0} small />
          <span className="chats-conv-titles">
            <span className="chats-conv-name">CleanMyBed</span>
            <span className="chats-conv-sub">{busy ? 'typing…' : `Practice chat${status ? `, ${status}` : ''}`}</span>
          </span>
        </div>
        <button type="button" className="chats-icon-btn" aria-label="Menu" onClick={menu}><MoreIcon /></button>
      </header>
      <div className="chats-thread-wrap">
        <div className="chats-thread" ref={box}>
          <div className="chats-sys">Practice chat. You are the customer: ask CleanMyBed anything. Leo answers. Tap 👍 or 👎 under each reply. Nothing reaches a real customer.</div>
          {blocked && <div className="chats-sys">{blocked}</div>}
          {turns.map((t) => t.role === 'hint' ? <div key={t.id} className="chats-sys">{t.text}</div> : (
            <div key={t.id} className="chats-msg-row">
              <div className={`chats-msg-wrap${t.role === 'customer' ? ' chats-msg-wrap--out' : ''}`}>
                <div className={`chats-msg ${t.role === 'customer' ? 'chats-msg--out' : 'chats-msg--in'}`}>
                  {/* As the customer sees it: the text, then the link's button (Gary, 17 Oct). */}
                  <span className="chats-msg-text">{splitLinkButton(t.text).text}</span>
                  {splitLinkButton(t.text).button && <div className="chats-msg-buttons"><div className="chats-msg-button">{splitLinkButton(t.text).button}</div></div>}
                </div>
              </div>
              {t.role === 'drc' && (
                <div className="chats-train-judge">
                  {t.verdict ? (
                    <span className={t.verdict === 'tick' ? 'chats-label chats-label--green' : 'chats-label chats-label--red'}>{t.verdict === 'tick' ? '👍 Good' : '👎 Noted'}</span>
                  ) : (
                    <>
                      <button type="button" className="chats-train-thumb" aria-label="Good reply" onClick={() => void judge(t, 'tick', '')}>👍</button>
                      <button type="button" className="chats-train-thumb" aria-label="Not a good reply" onClick={() => whyNotGood(t)}>👎</button>
                    </>
                  )}
                  {t.meta?.handed_over && <small>Handed to a person{t.meta.reason ? ` (${t.meta.reason})` : ''}</small>}
                </div>
              )}
            </div>
          ))}
          {busy && <div className="chats-typing" aria-label="Leo is typing"><i /><i /><i /></div>}
        </div>
      </div>
      <form className="chats-composer" onSubmit={(e) => void send(e)}>
        <input className="chats-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Message" aria-label="Your message as the customer" autoComplete="off" />
        <button type="submit" className="chats-send" aria-label="Send" disabled={busy || !typed.trim()}><SendIcon /></button>
      </form>
    </>
  );
}

// ---------------------------------------------------------------- step 1: wording

const VERDICT_LABEL: Record<string, { text: string; cls: string }> = {
  approve: { text: 'Approved', cls: 'chats-label chats-label--green' },
  reject: { text: 'Rejected', cls: 'chats-label chats-label--red' },
  edit: { text: 'Edit suggested', cls: 'chats-label chats-label--violet' },
};

function Wording({ items, mine, onSaved }: { items: ReviewItem[]; mine: MyVerdict[]; onSaved: (v: MyVerdict) => void }) {
  const groups = useMemo(() => {
    const out = new Map<string, ReviewItem[]>();
    for (const i of items) out.set(i.group, [...(out.get(i.group) ?? []), i]);
    return [...out.entries()];
  }, [items]);
  const done = new Set(mine.map((m) => `${m.target_type}:${m.target_ref}`));
  return (
    <>
      <p className="chats-help-intro">{done.size} of {items.length} checked.</p>
      {groups.map(([group, list]) => (
        <section key={group}>
          <p className="chats-group-title">{group}</p>
          <div className="chats-group">
            {list.map((item) => <WordingItem key={`${item.type}:${item.ref}`} item={item} last={[...mine].reverse().find((m) => m.target_type === item.type && m.target_ref === item.ref) ?? null} onSaved={onSaved} />)}
          </div>
        </section>
      ))}
    </>
  );
}

function WordingItem({ item, last, onSaved }: { item: ReviewItem; last: MyVerdict | null; onSaved: (v: MyVerdict) => void }) {
  const { toast } = useOverlay();
  const [editing, setEditing] = useState<'reject' | 'edit' | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  async function save(verdict: 'approve' | 'reject' | 'edit') {
    if (busy) return;
    setBusy(true);
    try {
      const suggestion = verdict === 'edit' ? typed.trim() : undefined;
      const comment = verdict === 'reject' ? typed.trim() : undefined;
      await saveVerdict({ target_type: item.type, target_ref: item.ref, target_text: item.text, verdict, suggestion, comment });
      onSaved({ target_type: item.type, target_ref: item.ref, verdict, suggestion: suggestion ?? null, comment: comment ?? null });
      setEditing(null);
      setTyped('');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const label = last ? VERDICT_LABEL[last.verdict] : null;
  return (
    <div className="chats-set chats-train-item">
      <div className="chats-set-main">
        <b>{item.title}</b> {label && <span className={label.cls}>{label.text}</span>}
        <p className="chats-train-text">{item.text}</p>
        {item.note && <small>{item.note}</small>}
        {editing ? (
          <div className="chats-train-edit">
            <textarea
              className="chats-input chats-input--stack"
              rows={3}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={editing === 'edit' ? 'Your better wording' : 'Why? A few words'}
              aria-label={editing === 'edit' ? 'Suggested wording' : 'Reason'}
            />
            <div className="chats-train-actions">
              <button type="button" className="chats-btn" disabled={busy || !typed.trim()} onClick={() => void save(editing)}>Save</button>
              <button type="button" className="chats-btn chats-btn--ghost" onClick={() => { setEditing(null); setTyped(''); }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="chats-train-actions">
            <button type="button" className="chats-btn" disabled={busy} onClick={() => void save('approve')}>Approve</button>
            <button type="button" className="chats-btn chats-btn--ghost" onClick={() => { setEditing('edit'); setTyped(item.text); }}>Suggest edit</button>
            <button type="button" className="chats-btn chats-btn--ghost" onClick={() => setEditing('reject')}>Reject</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- results (head office)

function Results() {
  const [rows, setRows] = useState<ResultRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    loadResults().then((r) => setRows(r.verdicts)).catch((e: Error) => setError(e.message));
  }, []);
  if (error) return <p className="chats-empty">{error}</p>;
  if (!rows) return <p className="chats-empty">Loading...</p>;
  if (!rows.length) return <p className="chats-empty">No verdicts yet this round.</p>;
  const byTarget = new Map<string, ResultRow[]>();
  for (const r of rows) {
    const key = r.target_type === 'practice' ? `practice:${r.target_ref}` : `${r.target_type}:${r.target_ref}`;
    byTarget.set(key, [...(byTarget.get(key) ?? []), r]);
  }
  const people = new Set(rows.map((r) => r.reviewer));
  return (
    <>
      <p className="chats-help-intro">{rows.length} verdicts from {people.size} {people.size === 1 ? 'person' : 'people'}: {[...people].join(', ')}.</p>
      <div className="chats-group">
        {[...byTarget.entries()].map(([key, list]) => (
          <div key={key} className="chats-set chats-train-item">
            <div className="chats-set-main">
              <b>{list[0]!.target_type === 'practice' ? 'Practice reply' : list[0]!.target_type === 'comment' ? 'Comment on the screen' : list[0]!.target_ref}</b>
              <p className="chats-train-text">{list[0]!.target_text}</p>
              {list.map((r, i) => (
                <small key={i}>
                  {r.reviewer}: {r.verdict}{r.suggestion ? `, suggests "${r.suggestion}"` : ''}{r.comment ? `, "${r.comment}"` : ''}
                </small>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- stress test (head office), followed live

function Bench({ headOffice }: { headOffice: boolean }) {
  const { toast } = useOverlay();
  const [runId, setRunId] = useState<string | null>(null);
  const [turns, setTurns] = useState<BenchTurn[]>([]);
  const [rated, setRated] = useState<Record<string, 'tick' | 'cross'>>({});
  const [open, setOpen] = useState<string | null>(null);
  const { openSheet, closeSheet } = useOverlay();
  const [starting, setStarting] = useState(false);
  const [cards, setCards] = useState<Scorecard[]>([]);
  const [runs, setRuns] = useState<BenchRun[]>([]);

  // New messages every few seconds while a run is going: you can watch the chats unfold.
  useEffect(() => {
    let gone = false;
    const load = () => void loadBench(runId ?? undefined).then((r) => {
      if (gone) return;
      if (!runId && r.run_id) setRunId(r.run_id);
      setTurns(r.turns);
      setRated((old) => ({ ...r.rated, ...old }));
      setCards(r.scorecards ?? []);
      setRuns(r.runs ?? []);
    }).catch(() => null);
    load();
    const t = setInterval(load, 4000);
    return () => { gone = true; clearInterval(t); };
  }, [runId]);

  async function start(round?: 1 | 2) {
    setStarting(true);
    try {
      const r = await startBench(round);
      setTurns([]);
      setCards([]);
      setRunId(r.run_id);
      toast(round ? `Readiness test round ${round} started: ${r.personas.length} customers` : `Stress test started: ${r.personas.length} customers`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'The stress test did not start.');
    } finally {
      setStarting(false);
    }
  }

  async function rate(t: BenchTurn, verdict: 'tick' | 'cross', comment: string) {
    const mine = turns.filter((x) => x.persona === t.persona && x.turn < t.turn && x.role !== 'note');
    try {
      await saveVerdict({
        target_type: 'practice', target_ref: t.id, target_text: t.text, verdict, comment: comment || undefined,
        context: { bench: true, run_id: runId, persona: t.persona, before: mine.slice(-6).map((x) => `${x.role === 'customer' ? 'Customer' : 'Leo'}: ${x.text}`), model: t.meta.model ?? null, handed_over: t.meta.handed_over ?? false },
      });
      setRated((r) => ({ ...r, [t.id]: verdict }));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That did not save. Try again.');
    }
  }

  const personas = [...new Set(turns.map((t) => t.persona))];
  const judged = cards.filter((c) => c.scorer === 'AI judge');
  const mineCards = cards.filter((c) => c.mine);
  const average = (list: Scorecard[]) => list.length ? (list.reduce((t, c) => t + scoreTotal(c), 0) / list.length).toFixed(1) : null;
  const label = (r: BenchRun) => `${r.round ? `Round ${r.round}` : 'Stress test'}, ${new Date(r.started_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`;
  return (
    <>
      <p className="chats-help-intro">AI customers with different personalities and motives chat with the real Leo. Nothing reaches WhatsApp. Tap a customer to follow their chat; it updates as they go. In the readiness test an AI judge scores each chat out of 30 when it ends; then add your own score.</p>
      {headOffice && (
        <div className="chats-group chats-pad-group chats-train-start">
          <button type="button" className="chats-btn" disabled={starting} onClick={() => void start(1)}>{starting ? 'Starting...' : 'Readiness test, round 1 (scripted)'}</button>
          <button type="button" className="chats-btn" disabled={starting} onClick={() => void start(2)}>Readiness test, round 2 (agenda only)</button>
          <button type="button" className="chats-btn chats-btn--ghost" disabled={starting} onClick={() => void start()}>Run a general stress test</button>
        </div>
      )}
      {runs.length > 1 && (
        <div className="chats-chips chats-train-roles">
          {runs.map((r) => <button key={r.run_id} type="button" className={`chats-chip${r.run_id === runId ? ' active' : ''}`} onClick={() => { setTurns([]); setCards([]); setOpen(null); setRunId(r.run_id); }}>{label(r)}</button>)}
        </div>
      )}
      {judged.length > 0 && (
        <div className="chats-group">
          <div className="chats-set">
            <div className="chats-set-main">
              <b>Readiness: {average(judged)} / 30</b>
              <small>AI judge, {judged.length} of {personas.length} chats scored.{mineCards.length ? ` Your scores: ${average(mineCards)} / 30 over ${mineCards.length}.` : ''}</small>
            </div>
            <span className={judged.some((c) => c.critical) ? 'chats-label chats-label--red' : 'chats-label chats-label--green'}>
              {judged.filter((c) => c.critical).length} critical
            </span>
          </div>
        </div>
      )}
      <div className="chats-group">
        {personas.map((p) => {
          const mine = turns.filter((t) => t.persona === p);
          const brief = mine.find((t) => t.role === 'note' && t.turn === 0)?.text ?? p;
          const ended = mine.some((t) => t.role === 'note' && t.turn > 0);
          const handed = mine.some((t) => t.role === 'drc' && t.meta.handed_over === true);
          const stepped = mine.some((t) => t.role === 'drc' && String(t.meta.model ?? '').includes('opus'));
          const replies = mine.filter((t) => t.role === 'drc');
          const done = replies.filter((t) => rated[t.id]).length;
          const ai = cards.find((c) => c.persona === p && c.scorer === 'AI judge');
          const readiness = mine.some((t) => t.role === 'note' && t.turn === 0 && typeof t.meta.round === 'number');
          return (
            <div key={p}>
              <button type="button" className="chats-set" onClick={() => setOpen(open === p ? null : p)}>
                <div className="chats-set-main">
                  <b>{brief.split(':')[0]}</b>
                  {/* The labels wrap inside the card, so the score is never cut off on a small phone (mobile review, 19 Oct). */}
                  <div className="chats-labels">
                    <span className={ended ? 'chats-label chats-label--grey' : 'chats-label chats-label--green'}>{ended ? 'Finished' : 'Chatting'}</span>
                    {handed && <span className="chats-label chats-label--orange">Handed over</span>}
                    {stepped && <span className="chats-label chats-label--violet">Opus</span>}
                    {replies.length > 0 && <span className={done === replies.length ? 'chats-label chats-label--green' : 'chats-label chats-label--grey'}>Rated {done} of {replies.length}</span>}
                    {ai && <span className={ai.critical ? 'chats-label chats-label--red' : 'chats-label chats-label--blue'}>{ai.critical ? 'Critical' : `${scoreTotal(ai)} / 30`}</span>}
                  </div>
                  <small>{brief.split(':').slice(1).join(':').trim()}</small>
                </div>
              </button>
              {open === p && (
                <div className="chats-thread chats-train-thread">
                  {mine.filter((t) => t.role !== 'note' || t.turn > 0).map((t) => t.role === 'note' ? <div key={`${t.turn}n`} className="chats-sys">{t.text}</div> : (
                    <div key={t.turn} className="chats-msg-row">
                      <div className={`chats-msg-wrap${t.role === 'customer' ? '' : ' chats-msg-wrap--out'}`}>
                        <div className={`chats-msg ${t.role === 'customer' ? 'chats-msg--in' : 'chats-msg--out'}`}>
                          {t.role === 'drc' && <div className="chats-msg-label"><AiLeo suffix={String(t.meta.model ?? '').includes('opus') ? ' (Opus)' : ''} /></div>}
                          <span className="chats-msg-text">{splitLinkButton(t.text).text}</span>
                          {splitLinkButton(t.text).button && <div className="chats-msg-buttons"><div className="chats-msg-button">{splitLinkButton(t.text).button}</div></div>}
                        </div>
                      </div>
                      {t.role === 'drc' && (
                        <div className="chats-train-judge chats-train-judge--live">
                          {rated[t.id] ? (
                            <span className={rated[t.id] === 'tick' ? 'chats-label chats-label--green' : 'chats-label chats-label--red'}>{rated[t.id] === 'tick' ? '👍 Good' : '👎 Noted'}</span>
                          ) : (
                            <>
                              <button type="button" className="chats-train-thumb" aria-label="Good reply" onClick={() => void rate(t, 'tick', '')}>👍</button>
                              <button type="button" className="chats-train-thumb" aria-label="Not a good reply" onClick={() => openSheet(<TextSheet title="What was wrong with this reply?" placeholder="A few words: too long, wrong price, sounds like a robot..." onSend={async (why) => { await rate(t, 'cross', why); closeSheet(); }} />)}>👎</button>
                            </>
                          )}
                          <small>{[t.meta.handed_over === true ? `Handed to a person (${String(t.meta.reason ?? '')})` : '', Array.isArray(t.meta.tools) && t.meta.tools.length ? `Used: ${(t.meta.tools as string[]).join(', ')}` : ''].filter(Boolean).join('. ')}</small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* The readiness test's scorecards, once the chat has ended (18 Oct). */}
              {open === p && runId && ended && readiness && (
                <ScorePanel key={`${runId}:${p}:${ai ? 'ai' : 'none'}`} runId={runId} persona={p} cards={cards.filter((c) => c.persona === p)} onSaved={(c) => setCards((old) => [...old.filter((x) => !(x.persona === p && x.mine)), c])} />
              )}
            </div>
          );
        })}
        {!personas.length && <p className="chats-empty">No stress test yet.</p>}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- readiness scorecards (Gary, 18 Oct)

/** One chat's scorecards: the AI judge's and each reviewer's, then my own to fill in or change. */
function ScorePanel({ runId, persona, cards, onSaved }: { runId: string; persona: string; cards: Scorecard[]; onSaved: (c: Scorecard) => void }) {
  const { toast } = useOverlay();
  const mineCard = cards.find((c) => c.mine);
  const start = mineCard ?? cards.find((c) => c.scorer === 'AI judge');
  const [scores, setScores] = useState<Scores>(() => Object.fromEntries(SCORE_POINTS.map((p) => [p.key, start?.[p.key] ?? 3])) as Scores);
  const [critical, setCritical] = useState(mineCard?.critical ?? false);
  const [reason, setReason] = useState(mineCard?.critical_reason ?? '');
  const [notes, setNotes] = useState(mineCard?.notes ?? '');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await saveScore({ run_id: runId, persona, ...scores, critical, critical_reason: critical ? reason : '', notes });
      onSaved({ persona, scorer: 'You', mine: true, ...scores, critical, critical_reason: critical ? reason : null, notes: notes || null });
      toast('Score saved');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That did not save. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chats-train-scores">
      {cards.filter((c) => !c.mine).map((c) => (
        <div key={c.scorer} className="chats-group">
          <p className="chats-group-title">{c.scorer}: {scoreTotal(c)} / 30{c.critical ? ', critical failure' : ''}</p>
          <div className="chats-pad-group">
            <p className="chats-train-points">{SCORE_POINTS.map((p) => `${p.label} ${c[p.key]}`).join(' · ')}</p>
            {c.critical && c.critical_reason && <p className="chats-train-critical">{c.critical_reason}</p>}
            {c.notes && <p className="chats-train-notes">{c.notes}</p>}
          </div>
        </div>
      ))}
      <p className="chats-group-title">Your score: {scoreTotal(scores)} / 30{mineCard ? ' (saved)' : ', starting from the AI judge'}</p>
      <div className="chats-group chats-pad-group">
        {SCORE_POINTS.map((p) => (
          <div key={p.key} className="chats-train-point">
            <b>{p.label}</b>
            <small>{p.hint}</small>
            <div className="chats-train-scale" role="radiogroup" aria-label={p.label}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" role="radio" aria-checked={scores[p.key] === n} className={`chats-chip${scores[p.key] === n ? ' active' : ''}`} onClick={() => setScores((s) => ({ ...s, [p.key]: n }))}>{n}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="chats-group">
        <button type="button" className="chats-set" onClick={() => setCritical(!critical)}>
          <div className="chats-set-main">
            Critical failure
            <small>Another customer's details, a booking said as confirmed, a medical claim, or a request for a person ignored. Overrides the score.</small>
          </div>
          <Switch on={critical} label="Critical failure" />
        </button>
      </div>
      <div className="chats-group chats-pad-group">
        {critical && <textarea className="chats-input chats-input--stack" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What was the critical failure?" aria-label="Critical failure" />}
        <textarea className="chats-input chats-input--stack" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Problems and observations" aria-label="Problems and observations" />
        <button type="button" className="chats-btn" disabled={busy} onClick={() => void save()}>{mineCard ? 'Update my score' : 'Save my score'}</button>
      </div>
    </div>
  );
}
